import { clone, localDateKey, makeId, safeNumber } from "./storage";
import { normalizeFundingWorksheet, type FundingWorksheet } from "./funding-sheet";

export type FieldType = "date" | "number" | "text" | "select";
export type ConfigField = {
  key: string;
  label: string;
  type: FieldType;
  options?: string[];
};

export type StageStatus = "waiting" | "processing" | "done" | "historical" | "skipped";
export type Stage = {
  id?: string;
  name: string;
  status: StageStatus;
  config: ConfigField[];
  data: Record<string, string>;
  category?: ProjectEventCategory;
  guidance?: string;
};

export type Service = {
  id: string;
  type: string;
  name: string;
  amount: string;
  contact: string;
  progress: number;
  linkedContractId?: string;
  linkedProcurementId?: string;
};

export type ProjectEventStatus =
  | "todo"
  | "doing"
  | "waiting"
  | "done"
  | "paused"
  | "na";

export type ProjectEventCategory =
  | "approval"
  | "meeting"
  | "funding"
  | "procurement"
  | "contract"
  | "payment"
  | "construction"
  | "negotiation"
  | "inspection"
  | "settlement"
  | "archive"
  | "other";

export type ProjectEvent = {
  id: string;
  occurredOn: string;
  recordedAt: string;
  title: string;
  note?: string;
  status: ProjectEventStatus;
  category: ProjectEventCategory;
  amount?: string;
  organization?: string;
  contact?: string;
  nextStep?: string;
  dueDate?: string;
  completedAt?: string;
  sourceStageId?: string;
  sourceTodoId?: string;
  fields?: Array<{ id: string; label: string; type: FieldType; value: string }>;
};

export type FundingYearPlan = {
  id: string;
  year: string;
  requestedAmount: string;
  receivedAmount: string;
  receivedOn?: string;
  deadline: string;
  note?: string;
};

export type FundingSource = {
  id: string;
  source: "unit" | "district" | "cultural" | "other";
  name: string;
  /** 旧版字段，仅用于把原有记录迁移到年度资金计划。 */
  approvedAmount?: string;
  receivedAmount?: string;
  receivedOn?: string;
  annualPlans?: FundingYearPlan[];
};

export type ProjectNextAction = {
  title: string;
  status: "todo" | "waiting" | "paused";
  dueDate?: string;
  waitingFor?: string;
  note?: string;
};

export type PaymentRecord = {
  id: string;
  /** 资金使用申请交单时间；保留 date 字段以兼容既有记录。 */
  date: string;
  amount: string;
  kind: "oneTime" | "first" | "progress" | "final" | "warranty" | "other";
  note?: string;
  fundingPlanId?: string;
  scheduleId?: string;
  archiveReceiptConfirmed?: boolean;
  performanceConfirmationConfirmed?: boolean;
  financeExceptionConfirmed?: boolean;
  archiveReceiptStatus?: "complete" | "pending" | "notRequired";
  performanceConfirmationStatus?: "complete" | "pending";
};

export type FundingPlanPaymentLink = {
  procurementId: string;
  procurementName: string;
  contractId: string;
  contractName: string;
  party: string;
  payment: PaymentRecord;
};

export type ContractPaymentPlan = {
  id: string;
  ratio: string;
  amount?: string;
  /** 计划付款应使用的年度资金；实际交单时默认带入，仍可按实际情况调整。 */
  fundingPlanId?: string;
};

export type FundingPlanUnpaidLink = {
  procurementId: string;
  procurementName: string;
  contractId: string;
  contractName: string;
  party: string;
  plan: ContractPaymentPlan;
  planIndex: number;
  amount: number;
};

export type ContractRecord = {
  id: string;
  name: string;
  party: string;
  amount: string;
  amountStatus?: "confirmed" | "pending";
  finalPaymentPolicy?: "bothRequired" | "archiveDeferred" | "financeApproved";
  settlementStatus?: "pending" | "confirmed";
  settlementAuditAmount?: string;
  settlementAuditDate?: string;
  settlementNote?: string;
  signedOn?: string;
  paymentPlan?: ContractPaymentPlan[];
  payments: PaymentRecord[];
};

export type ProcurementBasisKind = "meeting" | "approval";
export type ProcurementForm = "compare" | "consultation" | "openTender" | "direct";
export type ProcurementResult = "normal" | "rejected" | "voided";

export type ProcurementPackage = {
  id: string;
  name: string;
  kind: "design" | "supervision" | "cost" | "agency" | "construction" | "other";
  status: ProjectEventStatus;
  contracts: ContractRecord[];
  /** 采购依据：会议纪要 / 请示签批。 */
  basis?: ProcurementBasisKind;
  /** 会议纪要编号，如 圆党会〔2026〕3号。 */
  minutesRef?: string;
  /** 采购形式：比选 / 竞争性磋商 / 公开招标 / 直接委托。 */
  procurementForm?: ProcurementForm;
  /** 发布采购公告日期。 */
  announceDate?: string;
  /** 评价日期。 */
  evaluationDate?: string;
  /** 中标公示日期。 */
  awardAnnounceDate?: string;
  /** 公示网站链接（中标公示）。 */
  awardLink?: string;
  /** 公告网站链接。 */
  announceLink?: string;
  /** 采购结果：正常 / 流标 / 废标。 */
  procurementResult?: ProcurementResult;
  /** 意向公开日期（竞争性磋商 / 公开招标）。 */
  intentAnnounceDate?: string;
  /** 请示签批：文件名称。 */
  approvalDocName?: string;
  /** 请示签批：日期。 */
  approvalDocDate?: string;
};

export type Project = {
  id: string | number;
  name: string;
  /** 兼容旧版：repair 作为历史分类继续保留。 */
  type: "special" | "odd" | "repair";
  projectKind?: "special" | "odd";
  /** 旧项目默认属于工程项目；服务项目复用事件、资金、合同与付款模型。 */
  projectClass?: "engineering" | "service";
  createDate: string;
  status?: "active" | "paused" | "completed" | "trashed";
  heritage?: boolean;
  fundingMode?: "single" | "mixed";
  estimatedAmount?: string;
  budgetReviewAmount?: string;
  approvalAmount?: string;
  funds?: FundingSource[];
  stages: Stage[];
  events?: ProjectEvent[];
  nextAction?: ProjectNextAction;
  services?: Service[];
  procurements?: ProcurementPackage[];
  fundingWorksheet?: FundingWorksheet;
  templateVersion?: number;
};

export const PROCUREMENT_SERVICE_TYPE: Record<ProcurementPackage["kind"], string> = {
  design: "设计单位",
  supervision: "监理单位",
  cost: "造价咨询",
  agency: "招标代理",
  construction: "施工单位",
  other: "其他",
};

export const contractPaymentProgress = (contract: ContractRecord) => {
  const amount = safeNumber(contract.amount);
  if (amount <= 0) return 0;
  const paid = (contract.payments || []).reduce((sum, payment) => sum + safeNumber(payment.amount), 0);
  return Math.min(100, Math.max(0, Math.round((paid / amount * 100 + Number.EPSILON) * 10) / 10));
};

export const syncContractParticipant = (
  project: Project,
  procurementId: string,
  contract: ContractRecord,
): Project => {
  const procurement = (project.procurements || []).find((item) => item.id === procurementId);
  const party = contract.party.trim();
  if (!procurement || !party) return project;
  const type = PROCUREMENT_SERVICE_TYPE[procurement.kind] || "其他";
  const services = project.services || [];
  const existingIndex = services.findIndex((service) =>
    service.linkedContractId === contract.id
    || (!service.linkedContractId && service.name.trim() === party && service.type === type));
  const existing = existingIndex >= 0 ? services[existingIndex] : undefined;
  const linkedService: Service = {
    ...existing,
    id: existing?.id || makeId(),
    type,
    name: party,
    amount: contract.amount,
    contact: existing?.contact || "",
    progress: contractPaymentProgress(contract),
    linkedContractId: contract.id,
    linkedProcurementId: procurementId,
  };
  const nextServices = [...services];
  if (existingIndex >= 0) nextServices[existingIndex] = linkedService;
  else nextServices.push(linkedService);
  return { ...project, services: nextServices };
};

export const updateContractWithParticipant = (
  project: Project,
  procurementId: string,
  contractId: string,
  update: (contract: ContractRecord) => ContractRecord,
): Project => {
  let updatedContract: ContractRecord | null = null;
  const nextProject = {
    ...project,
    procurements: (project.procurements || []).map((procurement) => {
      if (procurement.id !== procurementId) return procurement;
      return {
        ...procurement,
        contracts: procurement.contracts.map((contract) => {
          if (contract.id !== contractId) return contract;
          updatedContract = update(contract);
          return updatedContract;
        }),
      };
    }),
  };
  return updatedContract
    ? syncContractParticipant(nextProject, procurementId, updatedContract)
    : project;
};

export const unlinkContractParticipant = (project: Project, contractId: string): Project => ({
  ...project,
  services: (project.services || []).map((service) => service.linkedContractId === contractId ? {
    ...service,
    linkedContractId: undefined,
    linkedProcurementId: undefined,
  } : service),
});

export type ProjectRule = {
  id: string;
  tone: "info" | "attention" | "success";
  title: string;
  detail: string;
};

type TemplateNode = {
  name: string;
  category: ProjectEventCategory;
  guidance: string;
  fields?: ConfigField[];
};

export const PROJECT_TYPE_LABEL: Record<Project["type"], string> = {
  special: "专项资金工程",
  odd: "内部资金工程",
  repair: "内部资金工程",
};

export const EVENT_STATUS_LABEL: Record<ProjectEventStatus, string> = {
  todo: "待处理",
  doing: "进行中",
  waiting: "待外部反馈",
  done: "已完成",
  paused: "暂停",
  na: "不适用",
};

export const EVENT_CATEGORY_LABEL: Record<ProjectEventCategory, string> = {
  approval: "报批立项",
  meeting: "会议决策",
  funding: "资金评审",
  procurement: "采购比选",
  contract: "合同",
  payment: "付款",
  construction: "施工",
  negotiation: "洽商变更",
  inspection: "监督检查",
  settlement: "结算审核",
  archive: "归档",
  other: "其他",
};

const dateField = (key = "date", label = "完成时间"): ConfigField => ({
  key,
  label,
  type: "date",
});

const ODD_TEMPLATE: TemplateNode[] = [
  {
    name: "提出工程请示",
    category: "approval",
    guidance: "说明要做什么和预计预算。预计金额将用于提示上会类型。",
    fields: [dateField("requestDate", "请示日期"), { key: "estimate", label: "预计预算（元）", type: "number" }],
  },
  {
    name: "单位内部上会",
    category: "meeting",
    guidance: "5万元及以上建议党组会，5万元以下建议主任办公会；实际情况可自行调整。",
    fields: [dateField("meetingDate", "上会日期"), { key: "meetingType", label: "会议类型", type: "select", options: ["主任办公会", "党组会", "其他"] }],
  },
  {
    name: "预算评审",
    category: "funding",
    guidance: "由工程科和资产科联合办理，以评审结果作为最终预算。",
    fields: [dateField("reviewDate", "评审完成日期"), { key: "reviewAmount", label: "预算评审金额（元）", type: "number" }],
  },
  {
    name: "采购或比选",
    category: "procurement",
    guidance: "记录采购方式、公告、评选和结果。",
    fields: [dateField("resultDate", "确定结果日期"), { key: "method", label: "采购形式", type: "text" }],
  },
  {
    name: "签订合同",
    category: "contract",
    guidance: "可在项目资金页继续添加合同和付款。",
    fields: [dateField("signedOn", "合同签订日期"), { key: "contractAmount", label: "合同金额（元）", type: "number" }],
  },
  {
    name: "施工进场",
    category: "construction",
    guidance: "记录入场协调会、进场或开工时间。",
    fields: [dateField("entryDate", "进场日期")],
  },
  {
    name: "竣工验收",
    category: "construction",
    guidance: "记录竣工和验收结果。",
    fields: [dateField("acceptDate", "验收日期")],
  },
  {
    name: "结算审核",
    category: "settlement",
    guidance: "不足200万元的内部资金工程由工程科负责，工程科和资产科签批，资产科联系审计单位。",
    fields: [dateField("settlementDate", "审核完成日期"), { key: "finalAmount", label: "审定金额（元）", type: "number" }],
  },
  {
    name: "归档与尾款",
    category: "archive",
    guidance: "尾款支付前需确认已取得归档单，并完成合同履行确认单。",
    fields: [dateField("archiveDate", "归档日期")],
  },
];

const SPECIAL_TEMPLATE: TemplateNode[] = [
  {
    name: "方案设计",
    category: "approval",
    guidance: "记录方案完成和送审时间。",
    fields: [dateField("designDate", "方案完成日期")],
  },
  {
    name: "外部部门报批",
    category: "approval",
    guidance: "可记录市文物局、发改委、园林局或其他相关部门的初审、复核和补充材料。",
    fields: [dateField("submitDate", "报送日期"), { key: "department", label: "报送部门", type: "text" }],
  },
  {
    name: "事前评估／可行性研究",
    category: "approval",
    guidance: "记录组织单位和评估报告日期。",
    fields: [dateField("reportDate", "报告日期"), { key: "organizer", label: "组织单位", type: "text" }],
  },
  {
    name: "预算评审",
    category: "funding",
    guidance: "财政评审、预算评审为同一节点，记录评审确认函或报告。",
    fields: [dateField("reviewDate", "评审报告日期"), { key: "reviewAmount", label: "预算评审金额（元）", type: "number" }],
  },
  {
    name: "资金申请与批复",
    category: "funding",
    guidance: "记录批复时间。最终金额统一以预算评审金额为准；专项资金按年度在资金页记录申请、到账和支付。",
    fields: [dateField("approvalDate", "批复日期")],
  },
  {
    name: "资金到账",
    category: "funding",
    guidance: "资金到账指资金已经实际到达单位财务。分年度申请、分年度到账的金额请在资金页逐年记录。",
    fields: [dateField("firstReceivedDate", "首次到账日期")],
  },
  {
    name: "单位内部上会立项",
    category: "meeting",
    guidance: "记录主任办公会、党组会及同意由哪些科室继续办理。",
    fields: [dateField("meetingDate", "上会日期"), { key: "meetingType", label: "会议类型", type: "select", options: ["主任办公会", "党组会", "其他"] }],
  },
  {
    name: "设计、监理及第三方采购",
    category: "procurement",
    guidance: "可分别建立设计、监理、造价咨询、招标代理等采购包。",
    fields: [dateField("serviceResultDate", "确定结果日期")],
  },
  {
    name: "施工单位采购",
    category: "procurement",
    guidance: "记录采购形式、公告、评标和中标结果。",
    fields: [dateField("constructionResultDate", "中标结果日期")],
  },
  {
    name: "合同签订与首付款",
    category: "contract",
    guidance: "每份合同可以添加多笔付款，普通付款保持简洁。",
    fields: [dateField("signedOn", "合同签订日期")],
  },
  {
    name: "施工准备与开工",
    category: "construction",
    guidance: "记录协调会、备案、进场和开工。",
    fields: [dateField("startDate", "开工日期")],
  },
  {
    name: "洽商与变更",
    category: "negotiation",
    guidance: "洽商金额变化5万元及以上建议党组会，低于5万元建议主任办公会。",
    fields: [{ key: "changeAmount", label: "金额变化（元）", type: "number" }, dateField("meetingDate", "单位上会日期")],
  },
  {
    name: "竣工验收",
    category: "construction",
    guidance: "记录竣工和验收时间。",
    fields: [dateField("acceptDate", "验收日期")],
  },
  {
    name: "结算审核",
    category: "settlement",
    guidance: "根据预算评审金额判断区财政审核或自行委托。",
    fields: [dateField("settlementDate", "审核报告日期"), { key: "finalAmount", label: "审定金额（元）", type: "number" }],
  },
  {
    name: "归档与尾款",
    category: "archive",
    guidance: "尾款支付前需确认归档单和合同履行确认单。",
    fields: [dateField("archiveDate", "归档日期")],
  },
];

const HERITAGE_TEMPLATE: TemplateNode[] = [
  {
    name: "监督站备案",
    category: "inspection",
    guidance: "仅文物专项资金工程默认启用。",
    fields: [dateField("recordDate", "备案日期")],
  },
  {
    name: "监督站交底会",
    category: "inspection",
    guidance: "记录交底会时间和参加单位。",
    fields: [dateField("meetingDate", "交底会日期")],
  },
  {
    name: "监督站现场检查",
    category: "inspection",
    guidance: "现场检查可通过事件重复记录问题、整改期限和复查结果。",
    fields: [dateField("inspectionDate", "检查日期")],
  },
  {
    name: "监督站竣工手续",
    category: "inspection",
    guidance: "记录竣工相关备案或确认时间。",
    fields: [dateField("finishDate", "完成日期")],
  },
];

const SERVICE_TEMPLATE: TemplateNode[] = [
  {
    name: "需求与方案",
    category: "approval",
    guidance: "记录需求形成、方案编制、专家论证和方案确认。",
    fields: [dateField("planDate", "方案确认日期")],
  },
  {
    name: "内部决策",
    category: "meeting",
    guidance: "记录请示、征求意见和单位内部会议决策。",
    fields: [dateField("meetingDate", "决策日期")],
  },
  {
    name: "采购或比选",
    category: "procurement",
    guidance: "记录采购方式、公告、评选和结果。",
    fields: [dateField("resultDate", "确定结果日期"), { key: "method", label: "采购形式", type: "text" }],
  },
  {
    name: "合同签订",
    category: "contract",
    guidance: "合同、金额和付款记录在“资金合同”中统一管理。",
    fields: [dateField("signedOn", "合同签订日期")],
  },
  {
    name: "服务实施",
    category: "other",
    guidance: "通过事件记录持续沉淀沟通、会议、成果提交和问题处理。",
    fields: [dateField("startDate", "开始日期")],
  },
  {
    name: "成果验收",
    category: "settlement",
    guidance: "记录成果提交、验收意见和整改确认。",
    fields: [dateField("acceptDate", "验收日期")],
  },
  {
    name: "结算与归档",
    category: "archive",
    guidance: "记录结算、尾款和资料归档。",
    fields: [dateField("archiveDate", "归档日期")],
  },
];

const templateNodes = (
  kind: "special" | "odd",
  heritage = false,
  projectClass: "engineering" | "service" = "engineering",
): TemplateNode[] =>
    projectClass === "service"
      ? SERVICE_TEMPLATE
      : kind === "odd"
      ? ODD_TEMPLATE
      : heritage
        ? [
            ...SPECIAL_TEMPLATE.slice(0, 10),
            ...HERITAGE_TEMPLATE.slice(0, 3),
            ...SPECIAL_TEMPLATE.slice(10),
            HERITAGE_TEMPLATE[3],
          ]
        : SPECIAL_TEMPLATE;

export const templateStageNames = (
  kind: "special" | "odd",
  heritage = false,
  projectClass: "engineering" | "service" = "engineering",
) => templateNodes(kind, heritage, projectClass).map((node) => node.name);

export const makeTemplateStages = (
  kind: "special" | "odd",
  heritage = false,
  projectClass: "engineering" | "service" = "engineering",
): Stage[] =>
  templateNodes(kind, heritage, projectClass).map((node) => ({
    id: makeId(),
    name: node.name,
    status: "waiting",
    category: node.category,
    guidance: node.guidance,
    config: clone(node.fields || [dateField()]),
    data: {},
  }));

export const createProjectFromTemplate = (input: {
  name: string;
  kind: "special" | "odd";
  projectClass?: "engineering" | "service";
  heritage?: boolean;
  fundingMode?: "single" | "mixed";
  estimatedAmount?: string;
  fundingSource?: FundingSource["source"];
  startStageName?: string;
}): Project => {
  const projectClass = input.projectClass || "engineering";
  const source = input.kind === "odd" ? "unit" : input.fundingSource || "other";
  const stages = makeTemplateStages(input.kind, Boolean(input.heritage), projectClass);
  const startStageIndex = input.startStageName
    ? stages.findIndex((stage) => stage.name === input.startStageName)
    : -1;
  if (startStageIndex >= 0) {
    stages.forEach((stage, index) => {
      stage.status = index < startStageIndex
        ? "historical"
        : index === startStageIndex
          ? "processing"
          : "waiting";
    });
  }
  return {
    id: makeId(),
    name: input.name,
    type: input.kind,
    projectKind: input.kind,
    projectClass,
    createDate: localDateKey(),
    status: "active",
    heritage: projectClass === "engineering" && input.kind === "special" ? Boolean(input.heritage) : false,
    fundingMode: input.kind === "special" ? input.fundingMode || "single" : "single",
    estimatedAmount: input.estimatedAmount || "",
    stages,
    events: [],
    funds: [
      {
        id: makeId(),
        source,
        name: source === "unit" ? "单位自有资金" : source === "district" ? "区财政资金" : source === "cultural" ? "文物专项资金" : "其他专项资金",
        approvedAmount: "",
        receivedAmount: "",
        annualPlans: [],
      },
    ],
    services: [],
    procurements: [],
    templateVersion: 3,
  };
};

const firstDateValue = (stage: Stage) => {
  const dateFieldItem = stage.config.find(
    (field) => field.type === "date" && stage.data?.[field.key],
  );
  return dateFieldItem ? stage.data[dateFieldItem.key] : "";
};

export const normalizeProject = (project: Project): Project => {
  const kind = project.projectKind || (project.type === "special" ? "special" : "odd");
  const stages: Stage[] = Array.isArray(project.stages)
    ? project.stages.map((stage, index) => ({
        ...stage,
        id: stage.id || `legacy-stage-${project.id}-${index}`,
        status: (stage.status === "done" || stage.status === "processing" || stage.status === "historical" || stage.status === "skipped"
          ? stage.status
          : "waiting") as StageStatus,
        config: Array.isArray(stage.config) ? stage.config : [],
        data: stage.data || {},
      }))
    : [];
  const existingEvents = (Array.isArray(project.events) ? project.events : []).map((event, index) => ({
    ...event,
    id: event.id || `legacy-event-${project.id}-${index}`,
    recordedAt: event.recordedAt || `${event.occurredOn || project.createDate}T00:00:00.000Z`,
  }));
  const sourceIds = new Set(existingEvents.map((event) => event.sourceStageId).filter(Boolean));
  const migratedEvents: ProjectEvent[] = stages
    .flatMap((stage, index) => {
      const occurredOn = firstDateValue(stage);
      if (!occurredOn || sourceIds.has(stage.id)) return [];
      const migrated: ProjectEvent = {
        id: `legacy-event-${project.id}-${index}`,
        occurredOn,
        recordedAt: new Date().toISOString(),
        title: stage.name,
        note: "由旧版节点日期自动生成",
        status: stage.status === "done" ? "done" : "doing",
        category: stage.category || "other",
        completedAt: stage.status === "done" ? occurredOn : undefined,
        sourceStageId: stage.id,
      };
      return [migrated];
    });
  const funds = (Array.isArray(project.funds) ? project.funds : []).map((fund, fundIndex) => {
    const fundId = fund.id || `legacy-fund-${project.id}-${fundIndex}`;
    const existingPlans = Array.isArray(fund.annualPlans) ? fund.annualPlans : [];
    const legacyYear =
      fund.receivedOn?.slice(0, 4) ||
      project.createDate?.slice(0, 4) ||
      localDateKey().slice(0, 4);
    const hasLegacyMoney =
      safeNumber(fund.approvedAmount) > 0 ||
      safeNumber(fund.receivedAmount) > 0;
    const annualPlans = existingPlans.length
      ? existingPlans.map((plan, planIndex) => ({
          ...plan,
          id: plan.id || `legacy-funding-year-${fundId}-${planIndex}`,
          deadline: plan.deadline || `${plan.year}-11-30`,
        }))
      : hasLegacyMoney
        ? [{
            id: `legacy-funding-year-${fundId}-${legacyYear}`,
            year: legacyYear,
            requestedAmount: fund.approvedAmount || "",
            receivedAmount: fund.receivedAmount || "",
            receivedOn: fund.receivedOn,
            deadline: `${legacyYear}-11-30`,
            note: "由旧版资金记录自动迁移",
          }]
        : [];
    return { ...fund, id: fundId, annualPlans };
  });
  const services = (Array.isArray(project.services) ? project.services : []).map((service, index) => ({
    ...service,
    id: service.id || `legacy-service-${project.id}-${index}`,
  }));
  const procurements = (Array.isArray(project.procurements) ? project.procurements : []).map((procurement, procurementIndex) => {
    const procurementId = procurement.id || `legacy-procurement-${project.id}-${procurementIndex}`;
    return {
      ...procurement,
      id: procurementId,
      contracts: (Array.isArray(procurement.contracts) ? procurement.contracts : []).map((contract, contractIndex) => {
        const contractId = contract.id || `legacy-contract-${procurementId}-${contractIndex}`;
        return {
          ...contract,
          id: contractId,
          payments: (Array.isArray(contract.payments) ? contract.payments : []).map((payment, paymentIndex) => ({
            ...payment,
            id: payment.id || `legacy-payment-${contractId}-${paymentIndex}`,
          })),
        };
      }),
    };
  });
  return {
    ...project,
    projectKind: kind,
    projectClass: project.projectClass || "engineering",
    status: project.status || "active",
    heritage: Boolean(project.heritage),
    fundingMode: project.fundingMode || "single",
    budgetReviewAmount: project.budgetReviewAmount || project.approvalAmount || "",
    approvalAmount: undefined,
    funds,
    stages,
    events: [...existingEvents, ...migratedEvents],
    services,
    procurements,
    fundingWorksheet: project.fundingWorksheet ? normalizeFundingWorksheet(project.fundingWorksheet) : undefined,
    templateVersion: project.templateVersion || 3,
  };
};

export const projectProgress = (project: Project) => {
  const applicable = project.stages.filter(
    (stage) => stage.status !== "skipped" && stage.status !== "historical",
  );
  const done = applicable.filter((stage) => stage.status === "done").length;
  return {
    done,
    total: applicable.length,
    percent: applicable.length ? Math.round((done / applicable.length) * 100) : 0,
  };
};

export const getProjectLastEvent = (project: Project) =>
  [...(project.events || [])].sort((a, b) =>
    `${b.occurredOn}_${b.recordedAt}`.localeCompare(`${a.occurredOn}_${a.recordedAt}`),
  )[0];

export const getSuggestedNextAction = (project: Project): ProjectNextAction | undefined => {
  if (project.nextAction?.title) return project.nextAction;
  const nextStage = project.stages.find(
    (stage) => stage.status !== "done" && stage.status !== "historical" && stage.status !== "skipped",
  );
  return nextStage
    ? {
        title: nextStage.name,
        status: nextStage.status === "processing" ? "todo" : "todo",
        note: nextStage.guidance,
      }
    : undefined;
};

export const projectRules = (project: Project): ProjectRule[] => {
  const rules: ProjectRule[] = [];
  const kind = project.projectKind || (project.type === "special" ? "special" : "odd");
  const meetingBase = safeNumber(project.estimatedAmount || project.budgetReviewAmount);
  if (meetingBase > 0) {
    rules.push({
      id: "meeting",
      tone: meetingBase >= 50000 ? "attention" : "info",
      title: meetingBase >= 50000 ? "建议走党组会" : "建议走主任办公会",
      detail: `当前判断金额为 ${meetingBase.toLocaleString("zh-CN")} 元；5万元是默认提示阈值，实际流程可调整。`,
    });
  }
  if (project.projectClass === "service") {
    if (project.fundingMode === "mixed") {
      rules.push({
        id: "mixed",
        tone: "info",
        title: "多来源资金分别记录",
        detail: "分别建立资金来源和年度计划，付款时选择对应资金。",
      });
    }
    return rules;
  }
  const reviewed = safeNumber(project.budgetReviewAmount);
  if (reviewed > 0) {
    if (reviewed >= 2000000) {
      rules.push({
        id: "settlement",
        tone: "attention",
        title: "结算审核报区财政局",
        detail: "预算评审金额达到200万元，建设单位不自行委托结算审核。",
      });
    } else if (kind === "special") {
      rules.push({
        id: "settlement",
        tone: "info",
        title: "可自行委托造价咨询公司",
        detail: "该专项资金工程预算评审金额不足200万元。",
      });
    } else {
      rules.push({
        id: "settlement",
        tone: "info",
        title: "工程科与资产科办理",
        detail: "工程科负责并履行两科签批，资产科联系审计单位，结果归工程科管理。",
      });
    }
  }
  if (project.heritage) {
    rules.push({
      id: "heritage",
      tone: "info",
      title: "已启用文物口监督站流程",
      detail: "施工阶段可重复记录现场检查、整改和复查。",
    });
  }
  if (project.fundingMode === "mixed") {
    rules.push({
      id: "mixed",
      tone: "info",
      title: "拼盘资金按来源分别记录",
      detail: "分别建立资金来源和年度计划；记录付款时可选择对应年度资金。监督站是否适用由用户选择。",
    });
  }
  if ((project.events || []).some((event) => event.category === "negotiation")) {
    rules.push({
      id: "negotiation-materials",
      tone: "attention",
      title: "洽商报市文物局材料提示",
      detail: "单位上会同意后，准备盖章公函原件3份（注明联系人）和洽商记录单原件3份，邮寄或人工送至市文物局办公室。",
    });
  }
  return rules;
};

const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export const completeContractPlanAmounts = (
  contractAmount: string | number,
  enteredAmounts: Array<string | number>,
  count: number,
) => {
  const normalizedCount = Math.max(1, count);
  const total = safeNumber(contractAmount);
  const amounts = Array.from({ length: normalizedCount }, (_, index) =>
    roundMoney(safeNumber(enteredAmounts[index])));
  if (total <= 0) return amounts;
  if (normalizedCount === 1) return [roundMoney(total)];
  const previousTotal = amounts.slice(0, -1).reduce((sum, amount) => sum + amount, 0);
  amounts[normalizedCount - 1] = roundMoney(Math.max(0, total - previousTotal));
  return amounts;
};

export const fundingSummary = (project: Project) => {
  const approved = safeNumber(project.budgetReviewAmount || project.approvalAmount);
  const received = (project.funds || []).reduce(
    (sum, item) => sum + (
      item.annualPlans?.length
        ? item.annualPlans.reduce((planSum, plan) => planSum + safeNumber(plan.receivedAmount), 0)
        : safeNumber(item.receivedAmount)
    ),
    0,
  );
  const requested = (project.funds || []).reduce(
    (sum, item) => sum + (
      item.annualPlans?.length
        ? item.annualPlans.reduce((planSum, plan) => planSum + safeNumber(plan.requestedAmount), 0)
        : safeNumber(item.approvedAmount)
    ),
    0,
  );
  const contracts = (project.procurements || []).flatMap((item) => item.contracts || []);
  const contracted = contracts.reduce((sum, item) => sum + safeNumber(item.amount), 0);
  const paid = contracts
    .flatMap((item) => item.payments || [])
    .reduce((sum, item) => sum + safeNumber(item.amount), 0);
  const unpaid = roundMoney(Math.max(0, contracted - paid));
  return {
    approved: roundMoney(approved),
    requested: roundMoney(requested),
    received: roundMoney(received),
    contracted: roundMoney(contracted),
    paid: roundMoney(paid),
    unpaid,
  };
};

export const fundingPlanPayments = (project: Project, fundingPlanId: string): FundingPlanPaymentLink[] =>
  (project.procurements || [])
    .flatMap((procurement) => (procurement.contracts || []).flatMap((contract) =>
      (contract.payments || [])
        .filter((payment) => payment.fundingPlanId === fundingPlanId)
        .map((payment) => ({
          procurementId: procurement.id,
          procurementName: procurement.name,
          contractId: contract.id,
          contractName: contract.name,
          party: contract.party,
          payment,
        })),
    ))
    .sort((left, right) => right.payment.date.localeCompare(left.payment.date));

export const fundingPlanPaid = (project: Project, fundingPlanId: string) =>
  roundMoney(
    fundingPlanPayments(project, fundingPlanId)
      .reduce((sum, link) => sum + safeNumber(link.payment.amount), 0),
  );

/**
 * 仅列出已指定本年度资金、但尚未交单的付款节点。
 * 已有交单记录的节点不再列入，避免把下一年度的付款误算到当前年度。
 */
export const fundingPlanUnpaidSchedules = (project: Project, fundingPlanId: string): FundingPlanUnpaidLink[] =>
  (project.procurements || [])
    .flatMap((procurement) => (procurement.contracts || []).flatMap((contract) =>
      (contract.paymentPlan || []).flatMap((plan, planIndex) => {
        if (plan.fundingPlanId !== fundingPlanId) return [];
        const hasSubmittedPayment = (contract.payments || []).some((payment, paymentIndex) =>
          payment.scheduleId === plan.id || (!payment.scheduleId && paymentIndex === planIndex));
        if (hasSubmittedPayment) return [];
        const amount = plan.amount !== undefined && plan.amount !== ""
          ? safeNumber(plan.amount)
          : safeNumber(contract.amount) * safeNumber(plan.ratio) / 100;
        return [{
          procurementId: procurement.id,
          procurementName: procurement.name,
          contractId: contract.id,
          contractName: contract.name,
          party: contract.party,
          plan,
          planIndex,
          amount: roundMoney(amount),
        }];
      }),
    ))
    .sort((left, right) => left.contractName.localeCompare(right.contractName) || left.planIndex - right.planIndex);
